import { SelectField } from '@sbab-ui/components'
import { ChangeEvent, MouseEvent, useEffect, useRef } from 'react'
import { AppAction } from '../../appState.ts'
import { track } from '../../lib/tracking.ts'
import { camelToKebab } from '../../lib/utils.ts'
import FormWrapper from './FormWrapper.tsx'

interface Props {
  step: number
  id: string
  value: string
  appDispatch: React.Dispatch<AppAction>
  label: string
}

export default function FormWithDropdown({ id, value, appDispatch, label, step }: Readonly<Props>) {
  const inputRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    inputRef.current?.querySelector('select')?.focus()
  }, [inputRef])

  const submitButtonRef = useRef<HTMLButtonElement>(null)

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      submitButtonRef.current?.click()
    }
  }

  function createOptionsObjects(startYear: number) {
    const currentYear = new Date().getFullYear()
    const startOfCurrentDecade = currentYear - (currentYear % 10)
    const optionsObjects: Array<{
      id: number
      description: string
      value: string
      disabled: boolean
    }> = [{ id: 0, description: 'Välj årtioende', value: '', disabled: true }]
    let id = 1

    for (let year = startYear; year <= startOfCurrentDecade; year += 10) {
      const description = year === startYear ? `${startYear} eller tidigare` : year.toString()
      const value = year.toString()
      optionsObjects.push({ id, description, value, disabled: false })
      id++
    }

    return optionsObjects
  }

  const optionsObjects = createOptionsObjects(1920)

  function onSelectChange(event: ChangeEvent<HTMLSelectElement>) {
    const { value } = event.target
    track('user-input', camelToKebab(id))
    appDispatch({ type: 'inputValueChange', value })
  }

  function submitHandler(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    track('click', `next-button.${camelToKebab(id)}`)
    appDispatch({ type: 'goForward' })
  }

  const disabled = value === ''

  return (
    <FormWrapper
      id={id}
      appDispatch={appDispatch}
      step={step}
      label={label}
      inputRef={inputRef}
      submitButtonRef={submitButtonRef}
      submitHandler={submitHandler}
      disabled={disabled}
    >
      <SelectField
        defaultValue={value}
        block
        className="w-full sm:!max-w-[375px]"
        onChange={onSelectChange}
        onKeyPress={handleKeyPress}
        placeholder="Välj årtionde"
        ref={inputRef}
        inputClassName={value === '' ? '!text-neutral-65' : ''}
        aria-label="Välj årtionde"
      >
        {optionsObjects.map((decadeObj) => (
          <option key={decadeObj.id} value={decadeObj.value} disabled={decadeObj.disabled}>
            {decadeObj.description}
          </option>
        ))}
      </SelectField>
    </FormWrapper>
  )
}
